import {
  AddOutlined, ApartmentOutlined, CheckOutlined, DashboardOutlined, GroupsOutlined,
  NotificationsNoneOutlined, SearchOutlined, TodayOutlined,
} from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  BrandThemePreviewProvider, IdentityAvatar, StatusBadge, ValueBadge,
  brandThemePresets, designTokens, type ResolvedBrandTheme,
} from '../../../design-system';
import { brandThemeLabel } from '../model/organization';
import type { OrganizationSettings } from '../model/types';

type AppearancePreviewProps = {
  draft: OrganizationSettings;
  baseline: OrganizationSettings;
  resolvedTheme: ResolvedBrandTheme;
};
const samplePeople = [
  { name: '민서(김민서)', initial: '민', job: '프로덕트 디자이너', team: '디자인팀' },
  { name: '준(이준호)', initial: '준', job: '프론트엔드 엔지니어', team: '개발팀' },
  { name: '하나(박하나)', initial: '하', job: '피플 매니저', team: '피플팀' },
];
const sampleNavigation = [
  { icon: DashboardOutlined, label: '홈' },
  { icon: GroupsOutlined, label: '구성원', selected: true },
  { icon: ApartmentOutlined, label: '조직도' },
  { icon: TodayOutlined, label: '휴가' },
];

export function AppearancePreview({ draft, baseline, resolvedTheme }: AppearancePreviewProps) {
  const preset = brandThemePresets.find(({ id }) => id === draft.brandTheme.presetId);
  const roles = [
    ['주요', resolvedTheme.actionColor], ['보조', resolvedTheme.accentActionColor],
    ['메뉴', resolvedTheme.navigationSurfaceColor], ['바탕', resolvedTheme.pageCanvasColor],
  ];
  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box>
          <Typography component="h3" sx={{ fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 700 }}>{brandThemeLabel(draft.brandTheme)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{preset?.description}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: 0.5 }}>미리보기</Typography>
      </Stack>
      <BrandThemePreviewProvider setting={draft.brandTheme}>
        <Box sx={{ p: { xs: 1, sm: 2.5 }, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.neutralRaisedSurface, border: '1px solid', borderColor: designTokens.color.divider }}>
          <Box data-testid="brand-theme-preview" role="img" aria-label={`${brandThemeLabel(draft.brandTheme)} 적용 미리보기`}
            sx={{ border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.navigation, overflow: 'hidden', bgcolor: designTokens.color.canvas, boxShadow: designTokens.shadow.surface }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: designTokens.color.divider }}>
              {baseline.logoUrl ? (
                <Box component="img" src={baseline.logoUrl} alt="" sx={{ maxWidth: 110, height: 24, objectFit: 'contain' }} />
              ) : (
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <Box sx={{ width: 22, height: 22, display: 'grid', placeItems: 'center', borderRadius: designTokens.shape.sm, bgcolor: designTokens.color.action, color: designTokens.color.inverseText, fontWeight: 700, fontSize: 13 }}>{(draft.displayName || 'H').slice(0, 1)}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{draft.displayName || 'Heyday'}</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                <NotificationsNoneOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                <IdentityAvatar size={24}>민</IdentityAvatar>
              </Stack>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '42px minmax(0, 1fr)', sm: '104px minmax(0, 1fr)' } }}>
              <Stack data-testid="brand-theme-preview-navigation" spacing={0.5}
                sx={{ px: 0.75, py: 2, bgcolor: designTokens.navigation.surface, color: designTokens.navigation.text, borderRight: '1px solid', borderColor: designTokens.navigation.divider }}>
                {sampleNavigation.map(({ icon: Icon, label, selected }) => (
                  <Stack key={label} direction="row" spacing={0.75}
                    sx={{ alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, px: { xs: 0.5, sm: 1 }, py: 1.25, borderRadius: designTokens.shape.md, bgcolor: selected ? designTokens.navigation.selectedSurface : 'transparent', color: selected ? designTokens.navigation.selectedText : 'inherit' }}>
                    <Icon sx={{ fontSize: 17, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: selected ? 600 : 400 }}>{label}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: designTokens.color.pageCanvas, minWidth: 0 }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>구성원</Typography>
                  <Button data-testid="brand-theme-preview-primary" component="span" tabIndex={-1} variant="fill" size="small" startIcon={<AddOutlined />} sx={{ pointerEvents: 'none' }}>추가</Button>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', px: 1.5, py: 1.125, bgcolor: designTokens.color.canvas, border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.input, color: 'text.secondary' }}>
                  <SearchOutlined sx={{ fontSize: 18 }} /><Typography variant="caption">이름으로 동료 찾기</Typography>
                </Stack>
                <Box sx={{ bgcolor: designTokens.color.canvas, border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.navigation, overflow: 'hidden' }}>
                  {samplePeople.map((person, index) => (
                    <Stack key={person.name} direction="row" spacing={1.25}
                      sx={{ p: { xs: 1.25, sm: 1.5 }, alignItems: 'center', bgcolor: index === 0 ? designTokens.color.actionSoft : undefined, borderBottom: index < samplePeople.length - 1 ? '1px solid' : undefined, borderColor: designTokens.color.divider }}>
                      <IdentityAvatar size={32}>{person.initial}</IdentityAvatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{person.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{person.job}</Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}><ValueBadge label={person.team} tone="accent" /></Box>
                    </Stack>
                  ))}
                </Box>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: designTokens.color.actionDark }}>
                  <CheckOutlined sx={{ fontSize: 16 }} />
                  <Typography variant="caption">선택한 구성원 1명</Typography>
                  <Box sx={{ flex: 1 }} />
                  <StatusBadge label="재직 중" tone="success" />
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center', flexWrap: 'wrap' }} useFlexGap>
            {roles.map(([label, color]) => (
              <Stack key={label} direction="row" spacing={0.625} sx={{ alignItems: 'center' }}>
                <Box aria-hidden sx={{ width: 12, height: 12, borderRadius: designTokens.radius.circle, bgcolor: color, boxShadow: `inset 0 0 0 1px ${designTokens.color.tableCellDivider}` }} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Stack spacing={1.5} sx={{ pt: 2 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            <StatusBadge label="완료" tone="success" /><StatusBadge label="주의" tone="warning" /><StatusBadge label="오류" tone="danger" />
          </Stack>
        </Stack>
      </BrandThemePreviewProvider>
    </Stack>
  );
}
